import React from 'react';
import { Loader2, Calendar, MapPin, Users, DollarSign } from 'lucide-react';
import { cn } from '@/lib/utils';

// Skeleton Components for Loading States
export const SkeletonLine = ({ className }: { className?: string }) => (
  <div className={cn("animate-pulse bg-muted rounded", className)} />
);

export const SkeletonCircle = ({ className }: { className?: string }) => (
  <div className={cn("animate-pulse bg-muted rounded-full", className)} />
);

export const SkeletonCard = ({ className }: { className?: string }) => (
  <div className={cn("border rounded-lg p-4 space-y-3", className)}>
    <SkeletonLine className="h-4 w-3/4" />
    <SkeletonLine className="h-3 w-1/2" />
    <SkeletonLine className="h-3 w-2/3" />
    <SkeletonLine className="h-8 w-full" />
  </div>
);

// Event Card Skeleton
export const EventCardSkeleton = ({ className }: { className?: string }) => (
  <div className={cn("border rounded-lg overflow-hidden bg-card", className)}>
    {/* Image skeleton */}
    <div className="w-full h-48 bg-muted animate-pulse" />
    
    <div className="p-4 space-y-3">
      {/* Title */}
      <SkeletonLine className="h-5 w-4/5" />
      
      {/* Date */}
      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4 text-muted-foreground" />
        <SkeletonLine className="h-3 w-24" />
      </div>
      
      {/* Location */}
      <div className="flex items-center gap-2">
        <MapPin className="w-4 h-4 text-muted-foreground" />
        <SkeletonLine className="h-3 w-32" />
      </div>
      
      {/* Tickets and price */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-muted-foreground" />
          <SkeletonLine className="h-3 w-20" />
        </div>
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-muted-foreground" />
          <SkeletonLine className="h-4 w-16" />
        </div>
      </div>
      
      {/* Button */}
      <SkeletonLine className="h-9 w-full" />
    </div>
  </div>
);

// Spinner Component
interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  text?: string;
}

export const Spinner: React.FC<SpinnerProps> = ({ 
  size = 'md', 
  className,
  text 
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  return (
    <div className={cn("flex flex-col items-center gap-2", className)}>
      <Loader2 
        className={cn("animate-spin text-primary", sizeClasses[size])}
        aria-label="Loading"
      />
      {text && (
        <span className="text-sm text-muted-foreground" aria-live="polite">
          {text}
        </span>
      )}
    </div>
  );
};

// Progress Bar Component
interface ProgressBarProps {
  progress: number;
  className?: string;
  showPercentage?: boolean;
  text?: string;
  variant?: 'primary' | 'success' | 'warning' | 'error';
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  progress,
  className,
  showPercentage = false,
  text,
  variant = 'primary'
}) => {
  const variantClasses = {
    primary: 'bg-primary',
    success: 'bg-green-500',
    warning: 'bg-yellow-500',
    error: 'bg-red-500'
  };

  const clampedProgress = Math.max(0, Math.min(100, progress));

  return (
    <div className={cn("w-full", className)} role="progressbar" aria-valuenow={clampedProgress} aria-valuemin={0} aria-valuemax={100}>
      {text && (
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium">{text}</span>
          {showPercentage && (
            <span className="text-sm text-muted-foreground">{clampedProgress}%</span>
          )}
        </div>
      )}
      <div className="w-full bg-muted rounded-full h-2">
        <div
          className={cn(
            "h-2 rounded-full transition-all duration-300 ease-out",
            variantClasses[variant]
          )}
          style={{ width: `${clampedProgress}%` }}
        />
      </div>
    </div>
  );
};

// Loading Overlay Component
interface LoadingOverlayProps {
  isLoading: boolean;
  text?: string;
  className?: string;
  children: React.ReactNode;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  isLoading,
  text = "Loading...",
  className,
  children
}) => {
  return (
    <div className={cn("relative", className)}>
      {children}
      {isLoading && (
        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50 rounded-lg">
          <div className="text-center space-y-4">
            <Spinner size="lg" />
            <p className="text-sm font-medium text-foreground">{text}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Lazy Loading Component with Intersection Observer
interface LazyLoadProps {
  children: React.ReactNode;
  className?: string;
  fallback?: React.ReactNode;
  rootMargin?: string;
  threshold?: number;
}

export const LazyLoad: React.FC<LazyLoadProps> = ({
  children,
  className,
  fallback = <SkeletonCard />,
  rootMargin = '100px',
  threshold = 0.1
}) => {
  const [isInView, setIsInView] = React.useState(false);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const elementRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin,
        threshold
      }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [rootMargin, threshold]);

  React.useEffect(() => {
    if (isInView && !isLoaded) {
      // Simulate loading time for smooth UX
      const timer = setTimeout(() => {
        setIsLoaded(true);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isInView, isLoaded]);

  return (
    <div ref={elementRef} className={className}>
      {isLoaded ? children : fallback}
    </div>
  );
};

// Pulse Loader for Buttons
interface PulseLoaderProps {
  isLoading?: boolean;
  children: React.ReactNode;
  className?: string;
}

export const PulseLoader: React.FC<PulseLoaderProps> = ({
  isLoading = false,
  children,
  className
}) => {
  return (
    <div className={cn("relative", className)}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex space-x-1">
            <div className="w-2 h-2 bg-current rounded-full animate-pulse" />
            <div className="w-2 h-2 bg-current rounded-full animate-pulse [animation-delay:0.2s]" />
            <div className="w-2 h-2 bg-current rounded-full animate-pulse [animation-delay:0.4s]" />
          </div>
        </div>
      )}
      <div className={cn(isLoading && "opacity-0")}>
        {children}
      </div>
    </div>
  );
};